// Predefined car options grouped by category.
// Curated list used in admin form for quick selection when placing a car ad.
// Admin can still add custom options via the free-text field below the categories.
//
// The NL strings here are the CANONICAL identifiers stored in the database.
// To display in another language, use `translateOption` / `translateCategory`.

export const OPTION_CATEGORIES = [
  {
    label: "Airconditioning",
    items: [
      "Airconditioning",
      "Automatische klimaatregeling",
      "Automatische klimaatregeling, 2 zones",
      "Automatische klimaatregeling, 3 zones",
      "Automatische klimaatregeling, 4 zones",
    ],
  },
  {
    label: "Assistance System",
    items: [
      "Botswaarschuwing",
      "Dodehoekdetectie",
      "Emergency Brake Assist",
      "Grootlichtassistent",
      "Hill-Hold Control",
      "Lane Departure Warning Systeem",
      "Night view assist",
      "Snelheidsbeperkingsinstallatie",
      "Verkeersbordherkenning",
    ],
  },
  {
    label: "Centrale vergrendeling",
    items: [
      "Centrale deurvergrendeling met afstandsbediening",
      "Centrale vergrendeling",
      "Keyless Entry",
    ],
  },
  {
    label: "Comfort",
    items: [
      "Armsteun",
      "Elektrisch verstelbare buitenspiegels",
      "Elektrische achterklep",
      "Elektrische ruiten",
      "Head-up display",
      "Lederen stuurwiel",
      "Luchtvering",
      "Open dak",
      "Panoramisch dak",
      "Regensensor",
      "Schakelpaddles",
      "Schuifdeur",
      "Schuifdeur links",
      "Schuifdeur rechts",
      "Standverwarming",
      "Start/Stop systeem",
      "Stuurbekrachtiging",
      "Stuurwielverwarming",
      "Voorruitverwarming",
    ],
  },
  {
    label: "Cruise Control",
    items: ["Adaptieve Cruise Control", "Cruise control"],
  },
  {
    label: "Entertainment",
    items: [
      "Aanraakscherm",
      "Android Auto",
      "Apple CarPlay",
      "Digitale radio-ontvangst",
      "Geheel digitaal combi-instrument",
      "Inductieladen voor smartphones",
      "Muziekstreaming geïntegreerd",
      "Sound system",
      "Spraakbediening",
      "Televisie",
      "USB",
      "Wifi-hotspot",
    ],
  },
  {
    label: "Entertainment en Media",
    items: [
      "Bluetooth",
      "Boordcomputer",
      "CD",
      "Handsfree",
      "MP3",
      "Multifunctioneel stuur",
      "Navigatiesysteem",
      "Radio",
    ],
  },
  {
    label: "Extra",
    items: [
      "Aangepast voor mindervaliden",
      "Bagagerek",
      "Bagageruimte-afscheiding",
      "Batterijcertificaat",
      "Binnenspiegel automatisch dimmend",
      "Biodiesel conversie",
      "E10-geactiveerd",
      "Elektrische achterbankverstelling",
      "Elektronische parkeerrem",
      "Getinte ramen",
      "Katalysator",
      "Laden in twee richtingen",
      "Lichtmetalen velgen",
      "Luifel",
      "Noodwiel",
      "Pechset",
      "Range extender",
      "Rechtsgestuurd",
      "Reservewiel",
      "Rokerspakket",
      "Sfeerverlichting",
      "Skiluik",
      "Sneeuwbanden",
      "Spoiler",
      "Sportophanging",
      "Sportpakket",
      "Stalen velgen",
      "Trekhaak",
      "Tuning",
      "Verwarming zetels achter",
      "Vierseizoenenbanden",
      "Warmtepomp",
      "Wassysteem voor koplampen",
      "Wind deflector",
      "Winterpakket",
      "Zomerbanden",
    ],
  },
  {
    label: "Lichten",
    items: [
      "Adaptive lights",
      "Bi-Xenon koplampen",
      "Dagrijlichten",
      "Koplamp volledig LED",
      "Laserlicht",
      "LED dagrijverlichting",
      "LED verlichting",
      "Lichtsensor",
      "Mistlampen",
      "Verblindingsvrij grootlicht",
      "Xenon Lichten",
    ],
  },
  {
    label: "Parkeerhulp",
    items: [
      "360° camera",
      "Parkeerhulp achter",
      "Parkeerhulp automatisch",
      "Parkeerhulp met camera",
      "Parkeerhulp voor",
    ],
  },
  {
    label: "Stoelen",
    items: [
      "Achterbank 1/3 - 2/3",
      "Elektrische zetelverstelling",
      "Lendensteun",
      "Massagestoelen",
      "Neerklapbare passagiersstoel",
      "Sportzetels",
      "Stoelventilatie",
      "Zetelverwarming",
    ],
  },
  {
    label: "Veiligheid",
    items: [
      "ABS",
      "Alarm",
      "Automatische Tractie Controle",
      "Bandenspanningscontrole",
      "Electronic Stability Program",
    ],
  },
];

// Flat list for quick lookup
export const ALL_OPTIONS = OPTION_CATEGORIES.flatMap((c) => c.items);

// Translation map: NL canonical → { fr, en } label
// Unknown options (custom strings) fall back to the original NL text.
const OPTION_TRANSLATIONS = {
  // Airconditioning
  "Airconditioning": { fr: "Climatisation", en: "Air conditioning" },
  "Automatische klimaatregeling": { fr: "Climatisation automatique", en: "Automatic climate control" },
  "Automatische klimaatregeling, 2 zones": { fr: "Climatisation automatique 2 zones", en: "2-zone automatic climate control" },
  "Automatische klimaatregeling, 3 zones": { fr: "Climatisation automatique 3 zones", en: "3-zone automatic climate control" },
  "Automatische klimaatregeling, 4 zones": { fr: "Climatisation automatique 4 zones", en: "4-zone automatic climate control" },
  // Assistance System
  "Botswaarschuwing": { fr: "Avertissement de collision", en: "Collision warning" },
  "Dodehoekdetectie": { fr: "Détection d'angle mort", en: "Blind spot detection" },
  "Emergency Brake Assist": { fr: "Assistance au freinage d'urgence", en: "Emergency brake assist" },
  "Grootlichtassistent": { fr: "Assistant feux de route", en: "High beam assist" },
  "Hill-Hold Control": { fr: "Aide au démarrage en côte", en: "Hill-hold control" },
  "Lane Departure Warning Systeem": { fr: "Alerte franchissement de ligne", en: "Lane departure warning" },
  "Night view assist": { fr: "Vision nocturne", en: "Night view assist" },
  "Snelheidsbeperkingsinstallatie": { fr: "Limiteur de vitesse", en: "Speed limiter" },
  "Verkeersbordherkenning": { fr: "Reconnaissance des panneaux", en: "Traffic sign recognition" },
  // Centrale vergrendeling
  "Centrale deurvergrendeling met afstandsbediening": { fr: "Verrouillage centralisé à distance", en: "Remote central locking" },
  "Centrale vergrendeling": { fr: "Verrouillage centralisé", en: "Central locking" },
  "Keyless Entry": { fr: "Accès sans clé", en: "Keyless entry" },
  // Comfort
  "Armsteun": { fr: "Accoudoir", en: "Armrest" },
  "Elektrisch verstelbare buitenspiegels": { fr: "Rétroviseurs électriques", en: "Electric mirrors" },
  "Elektrische achterklep": { fr: "Hayon électrique", en: "Electric tailgate" },
  "Elektrische ruiten": { fr: "Vitres électriques", en: "Electric windows" },
  "Head-up display": { fr: "Affichage tête haute", en: "Head-up display" },
  "Lederen stuurwiel": { fr: "Volant en cuir", en: "Leather steering wheel" },
  "Luchtvering": { fr: "Suspension pneumatique", en: "Air suspension" },
  "Open dak": { fr: "Toit ouvrant", en: "Open roof" },
  "Panoramisch dak": { fr: "Toit panoramique", en: "Panoramic roof" },
  "Regensensor": { fr: "Capteur de pluie", en: "Rain sensor" },
  "Schakelpaddles": { fr: "Palettes au volant", en: "Paddle shifters" },
  "Schuifdeur": { fr: "Porte coulissante", en: "Sliding door" },
  "Schuifdeur links": { fr: "Porte coulissante gauche", en: "Left sliding door" },
  "Schuifdeur rechts": { fr: "Porte coulissante droite", en: "Right sliding door" },
  "Standverwarming": { fr: "Chauffage stationnaire", en: "Auxiliary heating" },
  "Start/Stop systeem": { fr: "Système Start/Stop", en: "Start/Stop system" },
  "Stuurbekrachtiging": { fr: "Direction assistée", en: "Power steering" },
  "Stuurwielverwarming": { fr: "Volant chauffant", en: "Heated steering wheel" },
  "Voorruitverwarming": { fr: "Pare-brise chauffant", en: "Heated windscreen" },
  // Cruise Control
  "Adaptieve Cruise Control": { fr: "Régulateur de vitesse adaptatif", en: "Adaptive cruise control" },
  "Cruise control": { fr: "Régulateur de vitesse", en: "Cruise control" },
  // Entertainment
  "Aanraakscherm": { fr: "Écran tactile", en: "Touchscreen" },
  "Android Auto": { fr: "Android Auto", en: "Android Auto" },
  "Apple CarPlay": { fr: "Apple CarPlay", en: "Apple CarPlay" },
  "Digitale radio-ontvangst": { fr: "Radio numérique (DAB)", en: "Digital radio (DAB)" },
  "Geheel digitaal combi-instrument": { fr: "Combiné entièrement numérique", en: "Fully digital instrument cluster" },
  "Inductieladen voor smartphones": { fr: "Recharge sans fil smartphone", en: "Wireless phone charging" },
  "Muziekstreaming geïntegreerd": { fr: "Streaming musical intégré", en: "Integrated music streaming" },
  "Sound system": { fr: "Système audio", en: "Sound system" },
  "Spraakbediening": { fr: "Commande vocale", en: "Voice control" },
  "Televisie": { fr: "Télévision", en: "Television" },
  "USB": { fr: "USB", en: "USB" },
  "Wifi-hotspot": { fr: "Point d'accès Wi-Fi", en: "Wi-Fi hotspot" },
  // Entertainment en Media
  "Bluetooth": { fr: "Bluetooth", en: "Bluetooth" },
  "Boordcomputer": { fr: "Ordinateur de bord", en: "On-board computer" },
  "CD": { fr: "Lecteur CD", en: "CD player" },
  "Handsfree": { fr: "Kit mains-libres", en: "Hands-free" },
  "MP3": { fr: "MP3", en: "MP3" },
  "Multifunctioneel stuur": { fr: "Volant multifonction", en: "Multifunction steering wheel" },
  "Navigatiesysteem": { fr: "Système de navigation", en: "Navigation system" },
  "Radio": { fr: "Radio", en: "Radio" },
  // Extra
  "Aangepast voor mindervaliden": { fr: "Adapté pour personnes à mobilité réduite", en: "Adapted for disabled drivers" },
  "Bagagerek": { fr: "Galerie de toit", en: "Roof rack" },
  "Bagageruimte-afscheiding": { fr: "Cloison de séparation", en: "Cargo partition" },
  "Batterijcertificaat": { fr: "Certificat batterie", en: "Battery certificate" },
  "Binnenspiegel automatisch dimmend": { fr: "Rétroviseur intérieur jour/nuit auto", en: "Auto-dimming rearview mirror" },
  "Biodiesel conversie": { fr: "Conversion biodiesel", en: "Biodiesel conversion" },
  "E10-geactiveerd": { fr: "Compatible E10", en: "E10 compatible" },
  "Elektrische achterbankverstelling": { fr: "Banquette arrière électrique", en: "Power-adjustable rear seat" },
  "Elektronische parkeerrem": { fr: "Frein de parking électronique", en: "Electronic parking brake" },
  "Getinte ramen": { fr: "Vitres teintées", en: "Tinted windows" },
  "Katalysator": { fr: "Catalyseur", en: "Catalytic converter" },
  "Laden in twee richtingen": { fr: "Recharge bidirectionnelle (V2L/V2G)", en: "Bidirectional charging (V2L/V2G)" },
  "Lichtmetalen velgen": { fr: "Jantes alliage", en: "Alloy wheels" },
  "Luifel": { fr: "Auvent", en: "Awning" },
  "Noodwiel": { fr: "Roue de secours", en: "Spare wheel" },
  "Pechset": { fr: "Kit de dépannage", en: "Emergency kit" },
  "Range extender": { fr: "Prolongateur d'autonomie", en: "Range extender" },
  "Rechtsgestuurd": { fr: "Conduite à droite", en: "Right-hand drive" },
  "Reservewiel": { fr: "Roue de secours", en: "Spare wheel" },
  "Rokerspakket": { fr: "Pack fumeur", en: "Smoker's pack" },
  "Sfeerverlichting": { fr: "Éclairage d'ambiance", en: "Ambient lighting" },
  "Skiluik": { fr: "Trappe à skis", en: "Ski hatch" },
  "Sneeuwbanden": { fr: "Pneus neige", en: "Snow tyres" },
  "Spoiler": { fr: "Aileron", en: "Spoiler" },
  "Sportophanging": { fr: "Suspension sport", en: "Sport suspension" },
  "Sportpakket": { fr: "Pack sport", en: "Sport package" },
  "Stalen velgen": { fr: "Jantes acier", en: "Steel wheels" },
  "Trekhaak": { fr: "Attelage", en: "Tow bar" },
  "Tuning": { fr: "Tuning", en: "Tuning" },
  "Verwarming zetels achter": { fr: "Sièges arrière chauffants", en: "Heated rear seats" },
  "Vierseizoenenbanden": { fr: "Pneus toutes saisons", en: "All-season tyres" },
  "Warmtepomp": { fr: "Pompe à chaleur", en: "Heat pump" },
  "Wassysteem voor koplampen": { fr: "Lave-phares", en: "Headlight washers" },
  "Wind deflector": { fr: "Déflecteur de vent", en: "Wind deflector" },
  "Winterpakket": { fr: "Pack hiver", en: "Winter package" },
  "Zomerbanden": { fr: "Pneus été", en: "Summer tyres" },
  // Lichten
  "Adaptive lights": { fr: "Phares adaptatifs", en: "Adaptive headlights" },
  "Bi-Xenon koplampen": { fr: "Phares Bi-Xenon", en: "Bi-Xenon headlights" },
  "Dagrijlichten": { fr: "Feux de jour", en: "Daytime running lights" },
  "Koplamp volledig LED": { fr: "Phares full LED", en: "Full LED headlights" },
  "Laserlicht": { fr: "Phares laser", en: "Laser light" },
  "LED dagrijverlichting": { fr: "Feux de jour LED", en: "LED daytime running lights" },
  "LED verlichting": { fr: "Éclairage LED", en: "LED lighting" },
  "Lichtsensor": { fr: "Capteur de luminosité", en: "Light sensor" },
  "Mistlampen": { fr: "Antibrouillards", en: "Fog lights" },
  "Verblindingsvrij grootlicht": { fr: "Feux de route anti-éblouissement", en: "Glare-free high beam" },
  "Xenon Lichten": { fr: "Phares Xenon", en: "Xenon headlights" },
  // Parkeerhulp
  "360° camera": { fr: "Caméra 360°", en: "360° camera" },
  "Parkeerhulp achter": { fr: "Aide au stationnement arrière", en: "Rear parking aid" },
  "Parkeerhulp automatisch": { fr: "Stationnement automatique", en: "Automatic parking" },
  "Parkeerhulp met camera": { fr: "Aide au stationnement avec caméra", en: "Parking aid with camera" },
  "Parkeerhulp voor": { fr: "Aide au stationnement avant", en: "Front parking aid" },
  // Stoelen
  "Achterbank 1/3 - 2/3": { fr: "Banquette arrière 1/3 - 2/3", en: "60/40 split rear seat" },
  "Elektrische zetelverstelling": { fr: "Réglage électrique des sièges", en: "Electric seat adjustment" },
  "Lendensteun": { fr: "Support lombaire", en: "Lumbar support" },
  "Massagestoelen": { fr: "Sièges massants", en: "Massage seats" },
  "Neerklapbare passagiersstoel": { fr: "Siège passager rabattable", en: "Folding passenger seat" },
  "Sportzetels": { fr: "Sièges sport", en: "Sport seats" },
  "Stoelventilatie": { fr: "Ventilation des sièges", en: "Ventilated seats" },
  "Zetelverwarming": { fr: "Sièges chauffants", en: "Heated seats" },
  // Veiligheid
  "ABS": { fr: "ABS", en: "ABS" },
  "Alarm": { fr: "Alarme", en: "Alarm" },
  "Automatische Tractie Controle": { fr: "Contrôle de traction automatique", en: "Automatic traction control" },
  "Bandenspanningscontrole": { fr: "Contrôle de pression des pneus", en: "Tyre pressure monitoring" },
  "Electronic Stability Program": { fr: "Programme de stabilité électronique (ESP)", en: "Electronic stability program (ESP)" },

  // ---------- Backward-compat synonyms (older car entries) ----------
  "Lederen zetels": { fr: "Sièges en cuir", en: "Leather seats" },
  "Lederen bekleding": { fr: "Sellerie cuir", en: "Leather upholstery" },
  "Verwarmde zetels voor": { fr: "Sièges avant chauffants", en: "Heated front seats" },
  "Verwarmde zetels achter": { fr: "Sièges arrière chauffants", en: "Heated rear seats" },
  "Geventileerde zetels": { fr: "Sièges ventilés", en: "Ventilated seats" },
  "Massagefunctie": { fr: "Fonction massage", en: "Massage function" },
  "Elektrische zetels": { fr: "Sièges électriques", en: "Electric seats" },
  "Geheugenzetels": { fr: "Sièges à mémoire", en: "Memory seats" },
  "Sportstoelen": { fr: "Sièges sport", en: "Sport seats" },
  "Stuurverwarming": { fr: "Volant chauffant", en: "Heated steering wheel" },
  "Privacy glass": { fr: "Vitres teintées", en: "Privacy glass" },
  "Schuifdak": { fr: "Toit ouvrant", en: "Sunroof" },
  "LED koplampen": { fr: "Phares LED", en: "LED headlights" },
  "Matrix LED koplampen": { fr: "Phares LED matriciels", en: "Matrix LED headlights" },
  "Premium geluidssysteem": { fr: "Système audio premium", en: "Premium sound system" },
  "Keyless entry & start": { fr: "Accès et démarrage sans clé", en: "Keyless entry & start" },
  "Digitaal dashboard": { fr: "Tableau de bord numérique", en: "Digital cockpit" },
  "Draadloos opladen": { fr: "Recharge sans fil", en: "Wireless charging" },
  "Achteruitrijcamera": { fr: "Caméra de recul", en: "Rear camera" },
  "Parkeersensoren voor & achter": { fr: "Capteurs de stationnement avant & arrière", en: "Front & rear parking sensors" },
  "Adaptieve cruise control": { fr: "Régulateur de vitesse adaptatif", en: "Adaptive cruise control" },
  "Lane assist": { fr: "Aide au maintien de voie", en: "Lane assist" },
  "Airco": { fr: "Climatisation", en: "Air conditioning" },
  "Climate control": { fr: "Climatisation", en: "Climate control" },
  "Verwarmde stoelen": { fr: "Sièges chauffants", en: "Heated seats" },
  "Sleutelloze toegang": { fr: "Accès sans clé", en: "Keyless entry" },
  "Cruise Control": { fr: "Régulateur de vitesse", en: "Cruise control" },
  "Parkeersensoren": { fr: "Capteurs de stationnement", en: "Parking sensors" },
  "Stoelverwarming": { fr: "Sièges chauffants", en: "Heated seats" },
  "Elektrische ramen": { fr: "Vitres électriques", en: "Electric windows" },
  "Elektrische spiegels": { fr: "Rétroviseurs électriques", en: "Electric mirrors" },
  "Xenon koplampen": { fr: "Phares au xénon", en: "Xenon headlights" },
  "Isofix": { fr: "Isofix", en: "Isofix" },
  "Start/Stop": { fr: "Start/Stop", en: "Start/Stop" },
  "DAB radio": { fr: "Radio DAB", en: "DAB radio" },
};

const CATEGORY_TRANSLATIONS = {
  "Airconditioning": { fr: "Climatisation", en: "Air conditioning" },
  "Assistance System": { fr: "Aides à la conduite", en: "Driver assistance" },
  "Centrale vergrendeling": { fr: "Verrouillage centralisé", en: "Central locking" },
  "Comfort": { fr: "Confort", en: "Comfort" },
  "Cruise Control": { fr: "Régulateur de vitesse", en: "Cruise control" },
  "Entertainment": { fr: "Multimédia", en: "Entertainment" },
  "Entertainment en Media": { fr: "Multimédia & connectivité", en: "Entertainment & media" },
  "Extra": { fr: "Extras", en: "Extras" },
  "Lichten": { fr: "Éclairage", en: "Lighting" },
  "Parkeerhulp": { fr: "Aide au stationnement", en: "Parking assistance" },
  "Stoelen": { fr: "Sièges", en: "Seats" },
  "Veiligheid": { fr: "Sécurité", en: "Safety" },
  // legacy categories (still referenced by older code paths)
  "Comfort & Interieur": { fr: "Confort & Intérieur", en: "Comfort & Interior" },
  "Exterieur & Dak": { fr: "Extérieur & Toit", en: "Exterior & Roof" },
  "Tech & Navigatie": { fr: "Tech & Navigation", en: "Tech & Navigation" },
  "Veiligheid & Rijassistentie": { fr: "Sécurité & Aide à la conduite", en: "Safety & Driver assist" },
};

export function translateOption(option, lang, runtimeMap) {
  if (!lang || lang === "nl" || !option) return option;
  // 1) Per-car runtime map from backend (auto-translated on save)
  if (runtimeMap && runtimeMap[option] && runtimeMap[option][lang]) {
    return runtimeMap[option][lang];
  }
  // 2) Static map (standard checklist + known synonyms)
  return (
    OPTION_TRANSLATIONS[option]?.[lang] ||
    OPTION_TRANSLATIONS[option.trim()]?.[lang] ||
    option
  );
}

export function translateCategory(category, lang) {
  if (!lang || lang === "nl") return category;
  return CATEGORY_TRANSLATIONS[category]?.[lang] || category;
}
